/**
 * WHAT AN EXPERIENCE STANDS ON. EventDetails loads the event, the viewer,
 * invoices and payment; this adds what every experience needs on top and
 * renders the one the event's theme names (or the time machine picks):
 *
 *   the clock    real, or the time machine's; ticks so Now/Next moves
 *   the stage    lib/stages.ts, with the experience's teaser date
 *   choices      the household's live picks, or the demo attendee's
 *   saving       the /choices API, refused while pretending
 *   the drawer   publishes the experience's rows to the site's login drawer
 */
import { Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  ChoiceChange,
  ChoicesState,
  EventDetailsResponse,
  EventMeResponse,
} from '@/lib/types';
import { fetchChoices, saveChoice as postChoice } from '@/lib/api';
import { nowWall, stageOf, wallAdd } from '@/lib/stages';
import { EXPERIENCES } from './registry';
import { usePreview, pretending, type Preview } from './kit/preview';
import { PreviewPanel } from './kit/PreviewPanel';
import { demoChoices } from './kit/demo';
import type { Viewer } from './types';

const DAY = 86_400_000;

/** Which experience applies, if any: the time machine's choice wins for staff. */
export function experienceKeyFor(
  details: EventDetailsResponse | null,
  preview: Preview
): string | null {
  const key = (preview.allowed && preview.xp) || details?.page?.theme?.key || null;
  return key && EXPERIENCES[key] ? key : null;
}

export { usePreview };

export function ExperienceHost(props: {
  experienceKey: string;
  preview: Preview;
  setPreview: (n: Partial<Omit<Preview, 'allowed'>>) => void;
  details: EventDetailsResponse;
  apiBaseUrl: string;
  me: EventMeResponse | null;
  onSignIn: () => void;
  onRegister: () => void;
  setRegistrationDress: (dress: import('@/lib/types').SheetDress | null) => void;
  invoiceSlot: ReactNode;
  shareUrl: string;
}) {
  const { experienceKey, preview, setPreview, details, apiBaseUrl, me } = props;
  const xp = EXPERIENCES[experienceKey]!;
  const event = details.event;
  const pretend = pretending(preview);
  const viewer: Viewer = preview.allowed ? preview.viewer : 'real';

  // The clock. A stage asked for lands on the experience's own moment for it,
  // else a sensible one from the event's dates.
  const [realNow, setRealNow] = useState(nowWall);
  useEffect(() => {
    const id = window.setInterval(() => setRealNow(nowWall()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const now = useMemo(() => {
    if (preview.at) return preview.at;
    if (!preview.stage) return realNow;
    const moment = xp.moments?.[preview.stage];
    if (moment) return moment;
    const start = event.start;
    const end = event.end ?? start;
    switch (preview.stage) {
      case 'announce':
        return wallAdd(start, -60 * DAY);
      case 'registration':
        return wallAdd(start, -30 * DAY);
      case 'before':
        return wallAdd(start, -7 * DAY);
      case 'live':
        return wallAdd(start, 60 * 60 * 1000);
      case 'recap':
        return wallAdd(end, 3 * DAY);
      case 'teaser':
        return xp.teaserFrom ?? wallAdd(end, 120 * DAY);
    }
  }, [preview.at, preview.stage, realNow, xp, event.start, event.end]);

  // Registration state follows the pretend clock too, or a recap preview would
  // still offer Reserve.
  const realState = details.state.kind;
  const movedClock = !!(preview.allowed && (preview.stage || preview.at));
  const stage = stageOf({
    now,
    start: event.start,
    end: event.end,
    // Only a moved clock moves registration: ?as=attendee alone is today.
    registrationOpen: movedClock ? preview.stage === 'registration' : realState === 'open',
    registrationNotYet: movedClock ? preview.stage === 'announce' : realState === 'not-yet',
    choicesCloseAt: details.page?.choicesCloseAt ?? null,
    teaserFrom: xp.teaserFrom ?? null,
  });

  // The household's choices: live for a real viewer, the demo's for ?as=attendee.
  const [live, setLive] = useState<ChoicesState | null>(null);
  const signedIn = !!me && viewer === 'real';
  useEffect(() => {
    if (!signedIn) return setLive(null);
    const c = new AbortController();
    fetchChoices(apiBaseUrl, event.id, c.signal)
      .then((s) => !c.signal.aborted && setLive(s))
      .catch(() => {});
    return () => c.abort();
  }, [apiBaseUrl, event.id, signedIn, me?.invoices.length]);
  const [demo, setDemo] = useState<ChoicesState | null>(null);
  useEffect(() => setDemo(viewer === 'attendee' ? demoChoices(details) : null), [viewer, details]);
  const choices = viewer === 'attendee' ? demo : viewer === 'guest' ? null : live;

  const saveChoice = async (change: ChoiceChange): Promise<string | null> => {
    if (viewer === 'attendee') {
      // The demo changes its mind without changing anybody's record.
      setDemo((d) =>
        d
          ? {
              ...d,
              people: d.people.map((p) =>
                change.kind === 'option'
                  ? { ...p, picks: { ...p.picks, [change.groupId]: change.toOptionPriceId } }
                  : { ...p, answers: { ...p.answers, [change.formFieldId]: change.value } }
              ),
            }
          : d
      );
      return null;
    }
    if (pretend) return 'Not saved: you are in the time machine.';
    try {
      const res = await postChoice(apiBaseUrl, event.id, change);
      setLive(res.state);
      return res.note ?? null;
    } catch (err) {
      return err instanceof Error ? err.message : 'That did not save. Try again.';
    }
  };

  const Page = xp.Page;
  return (
    <>
      <Suspense fallback={xp.Skeleton ? <xp.Skeleton /> : <div className="min-h-[60vh]" />}>
        <Page
          details={details}
          apiBaseUrl={apiBaseUrl}
          me={viewer === 'real' ? me : null}
          viewer={viewer}
          clock={{ now, stage, pretend }}
          choices={choices}
          saveChoice={saveChoice}
          onSignIn={props.onSignIn}
          onRegister={props.onRegister}
          setRegistrationDress={props.setRegistrationDress}
          invoiceSlot={viewer === 'real' ? props.invoiceSlot : null}
          shareUrl={props.shareUrl}
        />
      </Suspense>
      <PreviewPanel
        preview={preview}
        set={setPreview}
        activeKey={details.page?.theme?.key ?? null}
      />
    </>
  );
}
